import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Loader2 } from 'lucide-react';
import { products } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { buildPathForLocale, getLocaleFromPathname } from '@/lib/intl-seo';
import { languages } from '@/lib/i18n';
import { ProductCard } from '@/components/products/ProductCard';
import { productIdFromCardClick, rememberAiQuery, trackAiEvent } from '@/lib/ai-search-track';
import { facetLabel, formatAiNumber } from '@/lib/ai-facet-labels';

const SUPPORTED_LANG = new Set(languages.map((l) => l.code));
const PAGE_SIZE = 24;

const CHIP_ORDER = [
  'maxPrice',
  'minPrice',
  'color',
  'gender',
  'season',
  'brand',
  'size',
  'category',
  'material',
  'feature',
  'style',
  'usage',
  'intent',
  'city',
];

const SERVICE_LINKS = [
  { re: /\b(ucak|uçak|ucus|uçuş|flight|flights|bilet)\b/i, seg: 'flights', labelKey: 'search.aiFlights' },
  { re: /\b(otel|oteller|hotel|hotels)\b/i, seg: 'hotels', labelKey: 'search.aiHotels' },
  { re: /\b(araç kiralama|arac kiralama|rent a car|car rental)\b/i, seg: 'car-rentals', labelKey: 'search.aiCars' },
  { re: /\b(restoran|restaurant)\b/i, seg: 'restaurants', labelKey: 'search.aiRestaurants' },
  { re: /\b(market|süpermarket|supermarket)\b/i, seg: 'supermarkets', labelKey: 'search.aiMarkets' },
];

const chipLabel = (key, value, t, lang) => {
  if (key === 'maxPrice') {
    return t('search.aiPriceUnder', '{{price}} TL altı', { price: formatAiNumber(value, lang) });
  }
  if (key === 'minPrice') {
    return t('search.aiPriceOver', '{{price}} TL üzeri', { price: formatAiNumber(value, lang) });
  }
  if (key === 'size') return t('search.aiSize', 'Beden {{size}}', { size: value });
  return facetLabel(value, lang);
};

const chipsFromFilters = (filters, t, lang) => {
  if (!filters || typeof filters !== 'object') return [];
  const chips = [];
  for (const key of CHIP_ORDER) {
    const value = filters[key];
    if (value == null || value === '') continue;
    chips.push(chipLabel(key, value, t, lang));
  }
  return chips;
};

const AiModePage = () => {
  const { lang } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [answer, setAnswer] = useState(null);
  const nextPageRef = useRef(2);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);

  const fromPath = typeof window !== 'undefined' ? getLocaleFromPathname(window.location.pathname) : '';
  const langCode =
    (lang && SUPPORTED_LANG.has(lang) && lang) ||
    (fromPath && SUPPORTED_LANG.has(fromPath) && fromPath) ||
    (i18n.language || 'tr').substring(0, 2);

  const firstName = String(user?.name || '').trim().split(/\s+/)[0];
  const greeting = firstName
    ? t('search.aiHelloName', 'Merhaba {{name}}, ne aramak istersin?', { name: firstName })
    : t('search.aiHello', 'Merhaba, ne aramak istersin?');

  const asked = (searchParams.get('q') || '').trim();

  useEffect(() => {
    rememberAiQuery(asked);
  }, [asked]);

  useEffect(() => {
    const q = asked;
    if (q.length < 2) {
      setAnswer(null);
      setHasMore(false);
      setLoading(false);
      return undefined;
    }
    setDraft(q);
    let cancelled = false;
    setLoading(true);
    products
      .aiSearch({ query: q, page: 1, limit: PAGE_SIZE, lang: langCode })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        const batch = data.products || [];
        const page = Number(data.page) || 1;
        const pages = Number(data.pages) || 1;
        const chips = chipsFromFilters(data.filters, t, langCode);
        const total = Number(data.total) || batch.length;
        const services = SERVICE_LINKS.filter((item) => item.re.test(q));
        let text;
        if (total > 0 && chips.length) {
          text = t(
            'search.aiAnswerFilters',
            '{{chips}} için {{total}} ürün buldum. Bir ürüne gidebilir ya da tüm listeyi açabilirsin.',
            { chips: chips.join(', '), total },
          );
        } else if (total > 0) {
          text = t('search.aiAnswerPlain', '"{{query}}" için {{total}} ürün buldum.', { query: q, total });
        } else if (services.length) {
          text = t(
            'search.aiAnswerService',
            'Bunu bir hizmet araması olarak aldım. Aşağıdaki sayfaya geçebilirsin.',
          );
        } else {
          text = t(
            'search.aiAnswerEmpty',
            'Bu cümleyle ürün çıkmadı. Kelimeleri sadeleştirip yeniden sorabilirsin.',
          );
        }
        setAnswer({
          query: q,
          text,
          chips,
          products: batch,
          total,
          services,
          city: Boolean(data.filters?.city),
        });
        setHasMore(batch.length >= PAGE_SIZE && page < pages);
        nextPageRef.current = page + 1;
      })
      .catch(() => {
        if (cancelled) return;
        setHasMore(false);
        setAnswer({
          query: q,
          text: t(
            'search.aiAnswerFailed',
            'Cümle şu an çözülemedi. Aynı kelimelerle normal ürün aramasını açabilirsin.',
          ),
          chips: [],
          products: [],
          total: 0,
          services: SERVICE_LINKS.filter((item) => item.re.test(q)),
          city: false,
          failed: true,
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [asked, langCode, t]);

  const loadMore = useCallback(async () => {
    if (!asked || !hasMore || loading || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const pageNum = nextPageRef.current;
    try {
      const res = await products.aiSearch({
        query: asked,
        page: pageNum,
        limit: PAGE_SIZE,
        lang: langCode,
      });
      const data = res.data || {};
      const batch = data.products || [];
      const page = Number(data.page) || pageNum;
      const pages = Number(data.pages) || page;
      setAnswer((prev) => {
        if (!prev) return prev;
        const seen = new Set(prev.products.map((item) => item.id || item.slug));
        const merged = [...prev.products];
        for (const item of batch) {
          const key = item.id || item.slug;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(item);
          }
        }
        return { ...prev, products: merged, total: Number(data.total) || prev.total };
      });
      setHasMore(batch.length >= PAGE_SIZE && page < pages);
      nextPageRef.current = pageNum + 1;
    } catch {
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [asked, hasMore, loading, langCode]);

  useEffect(() => {
    if (!hasMore) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: null, rootMargin: '480px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore, answer?.products?.length]);

  const submit = (event) => {
    event.preventDefault();
    const q = draft.trim();
    if (q.length < 2) return;
    setSearchParams({ q });
  };

  const listHref = `${buildPathForLocale(langCode, ['products'])}?search=${encodeURIComponent(asked)}&ai=1`;
  const plainHref = `${buildPathForLocale(langCode, ['products'])}?search=${encodeURIComponent(asked)}`;

  const box = (
    <form onSubmit={submit} className="w-full max-w-2xl">
      <div className="flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 shadow-md h-14">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('search.aiAsk', 'Soru sor')}
          className="h-full flex-1 min-w-0 border-0 bg-transparent text-base shadow-none outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 focus:!ring-0 focus-visible:!ring-0"
          data-testid="ai-mode-input"
          autoFocus
        />
        <button
          type="submit"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white disabled:opacity-30"
          disabled={draft.trim().length < 2 || loading}
          aria-label={t('common.search', 'Ara')}
          data-testid="ai-mode-submit"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </div>
    </form>
  );

  return (
    <div className="min-h-[70vh] bg-white" data-testid="ai-mode-page">
      <Helmet>
        <title>{t('search.aiButton', 'AI Modu')} | Turkish E-Market</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      {!asked ? (
        <div className="flex flex-col items-center justify-center px-4 pt-24 pb-16">
          <h1 className="mb-8 text-center text-2xl sm:text-3xl font-normal tracking-tight">{greeting}</h1>
          {box}
        </div>
      ) : (
        <div className="max-w-5xl mx-auto px-4 py-8">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            {t('search.aiYou', 'Sorun')}
          </p>
          <h1 className="text-xl sm:text-2xl font-medium mb-4">{asked}</h1>
          {loading && !answer ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading', 'Yükleniyor…')}
            </p>
          ) : null}
          {answer ? (
            <div data-testid="ai-mode-answer">
              <p className="text-base leading-relaxed max-w-3xl">{answer.text}</p>
              {answer.city ? (
                <p className="text-xs text-muted-foreground mt-2">
                  {t('search.aiCityNote', 'Şehir bilgisi listeyi daraltmaz.')}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2 mt-4" data-testid="ai-mode-links">
                {answer.chips.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-800"
                  >
                    {chip}
                  </span>
                ))}
                {answer.total > 0 ? (
                  <Link
                    to={listHref}
                    className="inline-flex items-center rounded-full border border-neutral-300 px-3 py-1 text-xs font-medium hover:bg-neutral-50"
                  >
                    {t('search.aiOpenList', 'Tüm ürün listesini aç')}
                  </Link>
                ) : null}
                {answer.failed ? (
                  <Link
                    to={plainHref}
                    className="inline-flex items-center rounded-full border border-neutral-300 px-3 py-1 text-xs font-medium hover:bg-neutral-50"
                  >
                    {t('search.aiOpenPlain', 'Normal aramayı aç')}
                  </Link>
                ) : null}
                {answer.services.map((item) => (
                  <Link
                    key={item.seg}
                    to={buildPathForLocale(langCode, [item.seg])}
                    className="inline-flex items-center rounded-full border border-neutral-300 px-3 py-1 text-xs font-medium hover:bg-neutral-50"
                  >
                    {t(item.labelKey)}
                  </Link>
                ))}
              </div>
              {answer.products.length > 0 ? (
                <div
                  className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6"
                  onClickCapture={(event) => {
                    const productId = productIdFromCardClick(event);
                    if (productId) trackAiEvent('click', productId);
                  }}
                >
                  {answer.products.map((product) => (
                    <ProductCard key={product.id || product.slug} product={product} />
                  ))}
                </div>
              ) : null}
              <div ref={sentinelRef} className="h-8 w-full" aria-hidden data-testid="ai-load-more" />
              {loadingMore ? (
                <p className="text-center text-sm text-muted-foreground py-4 flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('common.loading', 'Yükleniyor…')}
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="mt-8 flex justify-center">{box}</div>
        </div>
      )}
    </div>
  );
};

export default AiModePage;
